import React, { useEffect, useMemo, useState } from "react";
import { sendRequest } from "../../utils/api";
import { useParams } from "react-router-dom";
import BookedRoom from "../../models/BookedRoom";
import { dateDuration, formatDate } from "../../utils/date";
import { isEmptyObject } from "../../utils/helper-function";
import { padNumber } from "../../utils/string";
import { useModal } from "../../context/ConfirmModalContext";
import { useToast } from "../../context/ToastContext";


type Booking = {
  bookingId: string;
  bookingNumber: string;
  rooms: BookedRoom[];
};

type Guest = {
  name: string;
  email: string;
  phone: string;
};

type Data = {
  // billId: string;
  guest: Guest;
  checkIn: string;
  checkOut: string;
  nights: number;
  bookings: Booking[];
};

// const mockData: Data = {
//   // billId: "WCHS-234-442-2026",
//   guest: {
//     name: "Williams Court Guest",
//     email: "guest@example.com",
//     phone: "08123456789",
//   },
//   checkIn: "2026-04-20",
//   checkOut: "2026-04-23",
//   nights: 3,
//   bookings: [
//     {
//       bookingNumber: "234",
//       rooms: [
//         { number: "101", price: 20000 },
//         { number: "102", price: 20000 },
//       ],
//     },
//     {
//       bookingNumber: "442",
//       rooms: [{ number: "301", price: 30000 }],
//     },
//   ],
// };

export default function CheckInPage() {
    const { openModal, closeModal } = useModal();
    const { showToast } = useToast();

  const { billId } = useParams<{ billId: string }>();
  const [data, setData] = useState<Data>({
    guest: { name: "", email: "", phone: "" },
    checkIn: "",
    checkOut: "",
    nights: 0,
    bookings: [],
  });

  const bookRefLists = data.bookings.map(bk => padNumber(Number(bk.bookingNumber)));
  const bookRef = "WCHS-" + bookRefLists.join("-");

  useEffect(() => {
    const run = async () => {

      try {

        const response = await sendRequest("GET", `/bills/${billId}`);

        const bookings = response.bookings as {
          bookingId: string;
          bookingNumber: number;
          bookedRooms: BookedRoom[]
        }[];

        const bookedRoom = bookings[0]?.bookedRooms[0];
        const checkIn = bookedRoom?.checkInDate;
        const checkOut = bookedRoom?.checkOutDate;
        const nights = dateDuration(checkIn, checkOut);

        const mappedBookings = bookings.map(bk => ({
          bookingId: bk.bookingId,
          bookingNumber: padNumber(bk.bookingNumber),
          rooms: bk.bookedRooms,
        }));

        const data = {
          nights,
          checkIn: formatDate(checkIn),
          checkOut: formatDate(checkOut),
          guest: response.guest,
          bookings: mappedBookings,
        };

        setData(data);
      } catch (err: any) {
        console.log(err);
      }
    };
    run();

  }, []);


  // 🔥 flatten all rooms (for "check in all")
  // const allRoomsCheckedIn = useMemo(() => {
  //   return (data.bookings || []).every((b) =>
  //     b.rooms.every((r) => r.bookingType === "fullTime")
  //   );
  // }, [data.bookings]);



  if (!data || data.bookings.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center bg-white p-8 rounded-xl shadow">
          <h2 className="text-lg font-semibold text-gray-700">
            No Check-in Data Found
          </h2>
          <p className="text-sm text-gray-500 mt-2">
            This bill has no bookings or has already been processed.
          </p>

          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ✅ check-in single room
  const checkInRoom = async (
    bookedRoomId: string,
    bookingNumber: string,
    roomNumber: string
  ) => {

    console.log(bookedRoomId)
    try {
    await sendRequest("POST", `/bookings/${bookedRoomId}/checkin`)

      setData((prev) => ({
        ...prev,
        bookings: prev.bookings.map((b) =>
          b.bookingNumber === bookingNumber
            ? {
              ...b,
              rooms: b.rooms.map((r) =>
                r.roomNumber === roomNumber
                  ? { ...r, bookingType: "fullTime" }
                  : r
              ),
            }
            : b
        ),
      }));
    } catch (err: any) {
      if (err.status === 409) {
             return openModal(
                    "Booking Conflict ⚠️",
                    err?.response?.data?.error,
                    () => {
                        closeModal();
                        // window.location.href = "/login";
                    },
                    false,
                    "Close"
                );
      }
      // console.log(err.response);
    };
  }

  // ✅ check-in ALL rooms
  // const checkInAllRooms = () => {
  //   setData((prev) => ({
  //     ...prev,
  //     bookings: prev.bookings.map((b) => ({
  //       ...b,
  //       rooms: b.rooms.map((r) => ({
  //         ...r,
  //         bookingType: "fullTime",
  //       })),
  //     })),
  //   }));
  // };

  return (
    <div className="min-h-screen bg-gray-50 p-6 flex justify-center">
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow p-6 space-y-6">

        {/* HEADER */}
        <div>
          <h1 className="text-2xl font-bold">Room Check-In</h1>
          <p className="text-gray-500 text-sm">
            Bill ID:{" "}
            <span className="font-medium">{bookRef}</span>
          </p>
        </div>

        {/* GUEST INFO */}
        <div className="border rounded-xl p-4 bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-700 whitespace-nowrap">
              Guest Details
            </h2>
          </div>

          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Name</p>
              <p className="font-medium truncate">
                {data?.guest?.name}
              </p>
            </div>

            {data?.guest?.email && <div>
              <p className="text-gray-500">Email</p>
              <p className="font-medium truncate">
                {data?.guest?.email}
              </p>
            </div>}

            <div>
              <p className="text-gray-500">Phone</p>
              <p className="font-medium">
                {data?.guest?.phone}
              </p>
            </div>
          </div>
        </div>

        {/* STAY INFO */}
        <div className="bg-gray-100 rounded-xl p-4 flex justify-between text-sm">
          <div>
            <p className="text-gray-500">Check-in</p>
            <p className="font-medium">{data.checkIn}</p>
          </div>

          <div>
            <p className="text-gray-500">Check-out</p>
            <p className="font-medium">{data.checkOut}</p>
          </div>

          <div>
            <p className="text-gray-500">Nights</p>
            <p className="font-medium">{data.nights}</p>
          </div>
        </div>

        {/* BOOKINGS + ROOMS */}
        <div>
          <h2 className="text-lg font-semibold mb-3">
            Rooms
          </h2>

          {data.bookings.map((b) => (
            <div
              key={b.bookingNumber}
              className="border rounded-xl p-4 mb-4"
            >
              {/* booking header */}
              <div className="mb-3">
                <p className="text-sm text-gray-500">
                  Booking #{b.bookingNumber}
                </p>
              </div>

              {/* ROOMS */}
              <div className="space-y-3">
                {b.rooms.map((r) => (
                  <div
                    key={r.roomNumber}
                    className="flex items-center justify-between bg-gray-50 p-3 rounded-lg"
                  >
                    {/* room info */}
                    <div>
                      <p className="font-medium">
                        Room {r.roomNumber}
                      </p>
                      <p className="text-xs text-gray-500">
                        ₦{r?.amount?.toLocaleString()} / {data.nights} night
                      </p>
                    </div>

                    {/* status + action */}
                    <div className="flex items-center gap-3">
                      <span
                        className={`text-xs px-3 py-1 rounded-full ${r.bookingType === "fullTime"
                          ? "bg-green-100 text-green-700"
                          : "bg-yellow-100 text-yellow-700"
                          }`}
                      >
                        {r.bookingType === "fullTime"
                          ? "Checked In"
                          : "Pending"}
                      </span>

                      <button
                        disabled={r.bookingType.toLocaleLowerCase() === "fulltime"}
                        onClick={() =>
                          checkInRoom(
                            r.id,
                            b.bookingNumber,
                            r?.roomNumber || ""
                          )
                          // console.log(r)
                        }
                        className={`px-3 py-1 rounded-lg text-sm ${r.bookingType.toLocaleLowerCase() === "fulltime"
                            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                            : "bg-blue-600 text-white hover:bg-blue-700"
                          }`}
                      >
                        Check In
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* CHECK IN ALL */}
        {/* <button
          onClick={checkInAllRooms}
          disabled={allRoomsCheckedIn}
          className={`w-full py-3 rounded-xl font-semibold ${allRoomsCheckedIn
              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
              : "bg-green-600 text-white hover:bg-green-700"
            }`}
        >
          {allRoomsCheckedIn
            ? "All Rooms Checked In"
            : "Check In All Rooms"}
        </button> */}
      </div>
    </div>
  );
}